import { Disclosure, DisclosureButton, DisclosurePanel } from "@headlessui/react";
import { ChevronUpIcon } from "@heroicons/react/20/solid";
import { motion, useInView } from "framer-motion";
import React, { useRef } from "react";
import { FaCheckCircle, FaClock, FaDollarSign, FaShieldAlt, FaUsers, FaCode } from "react-icons/fa";
import { STANDARD_VARIANTS } from "../constants/animations";
import { CONTAINER_STYLES, TYPOGRAPHY_STYLES, CARD_STYLES } from "../constants/styles";
import useSmoothScroll from "../hooks/useSmoothScroll";

interface FAQItem {
  id: number;
  question: string;
  answer: string;
  category: "pricing" | "process" | "technical" | "experience";
  icon: React.ComponentType<any>;
}

const faqs: FAQItem[] = [
  {
    id: 1,
    question: "What's your typical project timeline and process?",
    answer: "Timelines depend on how defined the scope is. With a clear spec, development moves fast; for exploratory projects, we iterate together. As a rule of thumb: mobile apps take 2 weeks to 3 months, web apps 1 week to 2 months. You get regular updates either way.",
    category: "process",
    icon: FaClock,
  },
  {
    id: 2,
    question: "What's your pricing structure for different project types?",
    answer: "Pricing depends on scope: hourly rates for ongoing work, or a fixed quote for a well-defined project. Tell me what you're building and I'll come back with a concrete estimate.",
    category: "pricing",
    icon: FaDollarSign,
  },
  {
    id: 3,
    question: "How do you handle security and data protection?",
    answer: "I spent years doing cybersecurity R&D in the IDF's Unit 8200, and it shows in how I build: encrypted transport, secure authentication, and careful handling of user data from day one, not as an afterthought.",
    category: "technical",
    icon: FaShieldAlt,
  },
  {
    id: 4,
    question: "Do you work with startups or only established companies?",
    answer: "Mostly startups and growing companies, from early-stage teams that need an MVP to established businesses adding new products. I understand startup budgets and write code that scales as you grow.",
    category: "experience",
    icon: FaUsers,
  },
  {
    id: 5,
    question: "What technologies do you specialize in, and do you stay current?",
    answer: "I'm a one-man show who can handle almost everything: Flutter and mobile development, web apps with React, Python/Node.js backends, AI integration, cybersecurity, and deployment. I choose the right technology for your needs, not what's trendy. Recent work includes the open-source Mobile MCP framework and production LLM systems.",
    category: "technical",
    icon: FaCode,
  },
  {
    id: 6,
    question: "What happens after project delivery? Do you provide ongoing support?",
    answer: "Initial bug fixes and adjustments are part of delivery. After that, I offer maintenance packages for updates, scaling, and new features. Many clients keep working with me as their long-term technical partner.",
    category: "process",
    icon: FaCheckCircle,
  },
  {
    id: 7,
    question: "How do you communicate progress and handle changes during development?",
    answer: "You see progress as it happens: regular check-ins and access to a staging environment where you can test the latest build. Changes are welcome; I tell you what they mean for timeline and budget before we commit.",
    category: "process",
    icon: FaUsers,
  },
];

const iconStyles = {
  primary: "text-primary-600 bg-primary-50 dark:bg-primary-100/20 dark:text-accent-100",
  secondary: "text-primary-50 bg-primary-600 dark:bg-primary-400/20 dark:text-accent-100 "
};

const FAQItemComponent: React.FC<{ faq: FAQItem; index: number }> = ({ faq, index }) => {
  return (
    <motion.div
      variants={STANDARD_VARIANTS.item}
      className={`${CARD_STYLES.base} overflow-hidden`}
    >
      <Disclosure>
        {({ open }) => (
          <>
            <DisclosureButton className="flex justify-between w-full px-6 py-6 text-left focus:outline-none focus-visible:ring focus-visible:ring-primary-500 focus-visible:ring-opacity-75">
              <div className="flex items-start space-x-4">
                <div className={`p-2 rounded-lg ${index % 2 === 0 ? iconStyles.primary : iconStyles.secondary}`}>
                  <faq.icon className="w-5 h-5" />
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-accent-900 dark:text-surface-50 pr-4">
                    {faq.question}
                  </h3>
                </div>
              </div>
              <ChevronUpIcon
                className={`${open ? "rotate-180 transform" : ""
                  } h-6 w-6 text-accent-500 dark:text-accent-400 transition-transform duration-200 flex-shrink-0`}
              />
            </DisclosureButton>
            <DisclosurePanel className="px-6 pb-6">
              <div className="ml-16">
                <p className="text-accent-700 dark:text-accent-300 leading-relaxed max-w-prose">
                  {faq.answer}
                </p>
              </div>
            </DisclosurePanel>
          </>
        )}
      </Disclosure>
    </motion.div>
  );
};

const FAQ: React.FC = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, amount: 0.1 });
  const scrollToElement = useSmoothScroll();

  return (
    <motion.section
      ref={ref}
      className={CONTAINER_STYLES.section}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={STANDARD_VARIANTS.container}
    >
      <div className={CONTAINER_STYLES.content}>
        <motion.div variants={STANDARD_VARIANTS.item} className="text-center mb-16">
          <h2 className={TYPOGRAPHY_STYLES.sectionTitle}>
            Frequently Asked Questions
          </h2>
          <p className={TYPOGRAPHY_STYLES.sectionSubtitle}>
            Everything you need to know about working with me. Don't see your question?{" "}
            <a
              href="#contact"
              className="text-primary-600 dark:text-primary-400 hover:underline font-semibold"
              onClick={(e) => {
                e.preventDefault();
                scrollToElement("contact");
              }}
            >
              Ask me directly
            </a>
          </p>
        </motion.div>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <FAQItemComponent key={faq.id} faq={faq} index={index} />
          ))}
        </div>
      </div>
    </motion.section>
  );
};

export default FAQ;